import { useState } from "react";
import { useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  Paper,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Chip,
} from "@mui/material";

import useGetAutoNotifications from "../../hooks/useGetAutoNotifications";
import useGetNotificationPreferences from "../../hooks/useGetNotificationPreferences";
import Loader from "../../components/Loader";

dayjs.extend(utc);

const NotificationList = () => {
  const { _id_ } = useParams();

  const [search, setSearch] = useState({});
  const [isBeforeVisit, setIsBeforeVisit] = useState(true);

  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();
  setActive("notifications");
  setBreadcrumbTitle("Auto Notifications Log");

  const { data: notifications = { results: [], count: 0 }, isLoading } = useGetAutoNotifications({
    patientId: _id_,
    searchParams: search,
    isBeforeVisit,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const { data: preferences } = useGetNotificationPreferences(_id_);

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h6">Auto Notifications Log</Typography>

        {preferences && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.primary">
              Notification Preferences:
            </Typography>
            <Chip
              label={`Push: ${preferences.push ? "On" : "Off"}`}
              size="small"
              sx={{ backgroundColor: "#EE295D", color: "#fff" }}
            />
            <Chip
              label={`Email: ${preferences.email ? "On" : "Off"}`}
              size="small"
              sx={{ backgroundColor: "#EE295D", color: "#fff" }}
            />
            <Chip
              label={`SMS: ${preferences.sms ? "On" : "Off"}`}
              size="small"
              sx={{ backgroundColor: "#EE295D", color: "#fff" }}
            />
          </Stack>
        )}
      </Stack>

      <Stack
        direction="row"
        my={2}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Filters */}
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={search.category || ""}
              onChange={(e) =>
                setSearch((prev) => ({
                  ...prev,
                  category: e.target.value,
                  page: 1,
                }))
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="GENERAL_MESSAGE">GENERAL MESSAGE</MenuItem>
              <MenuItem value="TRIAL_VISIT_NOTIFICATION">TRIAL VISIT NOTIFICATION</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={search.type || ""}
              onChange={(e) =>
                setSearch((prev) => ({
                  ...prev,
                  type: e.target.value,
                  page: 1,
                }))
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="INAPP">IN APP</MenuItem>
              <MenuItem value="SMS">SMS</MenuItem>
              <MenuItem value="EMAIL">EMAIL</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Send Status</InputLabel>
            <Select
              label="Send Status"
              value={search.sent ?? ""}
              onChange={(e) =>
                setSearch((prev) => ({
                  ...prev,
                  sent: e.target.value,
                  page: 1,
                }))
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="True">True</MenuItem>
              <MenuItem value="False">False</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Before / During Toggle */}
        <Stack direction="row" spacing={1}>
          <Button
            variant={isBeforeVisit ? "contained" : "outlined"}
            size="small"
            onClick={() => setIsBeforeVisit(true)}
          >
            Before Visit Notifications
          </Button>
          <Button
            variant={!isBeforeVisit ? "contained" : "outlined"}
            size="small"
            onClick={() => setIsBeforeVisit(false)}
          >
            During Visit Notifications
          </Button>
        </Stack>
      </Stack>

      {isLoading && <Loader />}

      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: "80vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Category</TableCell>
              <TableCell sx={headerCellSx}>Type</TableCell>
              <TableCell sx={headerCellSx}>Sent</TableCell>
              <TableCell sx={headerCellSx}>Scheduled Time</TableCell>
              <TableCell sx={headerCellSx}>Description</TableCell>
              <TableCell sx={headerCellSx}>Visit Name</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!isLoading &&
              notifications.results.map((notification, index) => (
                <TableRow key={index} hover>
                  <TableCell>{notification.notification_type}</TableCell>
                  <TableCell>{notification.type}</TableCell>
                  <TableCell>{notification.sent ? "True" : "False"}</TableCell>
                  <TableCell>
                    {notification.scheduled_time
                      ? dayjs.utc(notification.scheduled_time).isValid()
                        ? dayjs
                            .utc(notification.scheduled_time)
                            .local()
                            .format("YYYY-MM-DD hh:mm A")
                        : "-"
                      : "-"}
                  </TableCell>
                  <TableCell>{notification.description}</TableCell>
                  <TableCell>{notification.visit_name}</TableCell>
                </TableRow>
              ))}

            {!isLoading && notifications.results.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No notifications found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 2 }}>
        <Typography variant="body2">
          Showing {notifications.results.length} of {notifications.count}
        </Typography>

        <Pagination
          color="primary"
          page={search.page || 1}
          count={Math.ceil(notifications.count / 10)}
          onChange={(_, page) => setSearch((prev) => ({ ...prev, page }))}
        />
      </Stack>
    </Paper>
  );
};

export default NotificationList;
